import React, { useEffect, useState } from "react";
import { Container, Form, Row, Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { ErrorMessage, Field, Formik, FieldProps } from "formik";
import { ExclamationCircle } from "react-bootstrap-icons";
import {
  useUserQuery,
  useUpdateUserMutation,
  useCountriesQuery,
  useDeleteUserMutation,
  useGetGeoByIpQuery,
} from "../../graphql/user.hooks";
import { profileSchema } from "./validation/profile";
import { CustomToast } from "../../components/toast";
import InputPassword from "./inputPassword";
import { ConfirmModal } from "../../components/modal/confirmDeleteProfile";

const Profile = () => {
  const { t } = useTranslation();
  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
  const [showToast, setShowToast] = useState<boolean>(false);
  const { data: countriesData, loading: countriesLading } = useCountriesQuery();
  const { data: userData, loading: userLoading } = useUserQuery();
  const [editProfile, { loading: editLoading }] = useUpdateUserMutation();
  const [deleteUser, { loading: deleteLoading }] = useDeleteUserMutation();
  const { data: geoData } = useGetGeoByIpQuery();

  useEffect(() => {
    document.title = "Profile";
  }, []);

  const initialValues = {
    firstName: userData?.user.firstName || "",
    lastName: userData?.user.lastName || "",
    phone: userData?.user.phone || "",
    email: userData?.user.email || "",
    countryId: userData?.user?.country?.id || "",
    newPassword: "",
    passwordConfirmed: "",
  };

  const trySaveChanges = async (values: any) => {
    if (values.newPassword !== values.passwordConfirmed) return;
    const user = Object.entries(values)
      .filter(([, value]) => value)
      .reduce((acc: any, [key, value]) => {
        acc[key] = value;
        return acc;
      }, {});
    user.countryId = +user.countryId;
    try {
      await editProfile({ variables: user });
      setShowToast(true);
    } catch (err) {
      console.error(err);
    }
  };

  const countries = countriesData?.countries || [];

  const tryDeleteAccount = async () => {
    setShowConfirmModal(false);
    try {
      const { data } = await deleteUser();
      if (data.deleteUser) {
        window.location.href = process.env.REACT_APP_ME_BOOKING_URL;
      }
    } catch (err) {
      console.error(err);
    }
  };
  if (userLoading) {
    return (
      <div className="d-flex align-items-center justify-content-center">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">{t("profile.loading")}...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <Container>
      <div>
        <h1 className="mb-5 fs-2">{t("menu.profile")}</h1>
      </div>
      <Formik
        initialValues={initialValues}
        validationSchema={profileSchema(t)}
        onSubmit={trySaveChanges}
        enableReinitialize
      >
        {({ handleSubmit, touched, errors }) => (
          <Form className="g-4 py-4 py-md-5 d-flex justify-content-center align-items-center bg-white rounded-4 position-relative">
            <div className="col-11 col-md-9 col-xl-8">
              <Row className="mb-4 mb-md-5 gx-3">
                <p className="fw-bold fs-4">{t("profile.general")}</p>
                <div className="mb-3 col-md-6">
                  <div className="form-floating ">
                    <Field
                      name="firstName"
                      type="text"
                      placeholder="First Name"
                      className={`form-control ps-3 ${
                        touched.firstName && errors.firstName
                          ? "is-invalid"
                          : ""
                      }`}
                    />
                    <label htmlFor="firstName" className="form-label ps-3 z-1">
                      {t("profile.firstName")}
                    </label>
                    <ErrorMessage
                      name="firstName"
                      component="div"
                      className="invalid-feedback"
                    />
                  </div>
                </div>
                <div className="mb-3 col-md-6">
                  <div className="form-floating">
                    <Field
                      name="lastName"
                      type="text"
                      placeholder="Last Name"
                      className={`form-control ps-3 ${
                        touched.lastName && errors.lastName ? "is-invalid" : ""
                      }`}
                    />
                    <label htmlFor="lastName" className="form-label ps-3 z-1">
                      {t("profile.lastName")}
                    </label>
                    <ErrorMessage
                      name="lastName"
                      component="div"
                      className="invalid-feedback"
                    />
                  </div>
                </div>
                <div className="mb-3 col-md-6">
                  <div className="form-floating">
                    <Field name="phone">
                      {({ field, form, meta }: FieldProps) => (
                        <>
                          <div
                            className={`custom-phone-input ${
                              meta.touched && meta.error ? "is-invalid" : ""
                            }`}
                          >
                            <PhoneInput
                              country={
                                field.value
                                  ? undefined
                                  : (geoData?.getGeoByIp?.countryIso2Code?.toLowerCase() ??
                                    "mx")
                              }
                              value={field.value}
                              onChange={(phone) => {
                                const normalizedPhone = phone.startsWith("+")
                                  ? phone
                                  : `+${phone}`;
                                form.setFieldValue("phone", normalizedPhone);
                              }}
                              inputProps={{
                                name: field.name,
                                required: true,
                              }}
                              enableSearch
                              specialLabel=""
                            />
                          </div>
                          <ErrorMessage
                            name="phone"
                            component="div"
                            className="invalid-feedback"
                          />
                        </>
                      )}
                    </Field>
                  </div>
                </div>

                <div className="mb-3 col-md-6">
                  <div className="form-floating">
                    <Field
                      readOnly
                      name="email"
                      type="email"
                      placeholder="Email"
                      className={`form-control ps-3 bg-soft-light-gray text-soft-gray ${
                        touched.email && errors.email ? "is-invalid" : ""
                      }`}
                    />
                    <label
                      htmlFor="email"
                      className="form-label ps-3 z-1 text-soft-gray"
                    >
                      {t("profile.email")}
                    </label>
                    <ErrorMessage
                      name="email"
                      component="div"
                      className="invalid-feedback"
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-floating position-relative d-flex align-items-center">
                    {countriesLading && (
                      <Spinner
                        animation="border"
                        role="status"
                        className="position-absolute"
                        style={{ right: "50px" }}
                      >
                        <span className="visually-hidden">
                          {t("profile.loading")}...
                        </span>
                      </Spinner>
                    )}
                    <Field
                      as="select"
                      name="countryId"
                      className={`form-select text-black d-flex align-items-center ps-3 ${
                        touched.country && errors.country ? "is-invalid" : ""
                      }`}
                    >
                      <option value="" disabled>
                        {t("profile.chooseCountry")}
                      </option>
                      {countries.map(({ id, name }) => (
                        <option key={id} value={id}>
                          {name}
                        </option>
                      ))}
                    </Field>
                    <label className="text-gray ps-3 z-1">
                      {t("profile.country")}
                    </label>
                    <ErrorMessage
                      name="country"
                      component="div"
                      className="invalid-feedback"
                    />
                  </div>
                </div>
              </Row>

              <Row className="mb-4 mb-md-5 gx-3">
                <p className="fw-bold fs-4">{t("profile.change")}</p>
                <div className="col-12 col-md-6">
                  <Field name="newPassword">
                    {({ field, meta, form }: FieldProps) => (
                      <InputPassword
                        label={t("profile.new")}
                        placeholder={t("profile.new")}
                        id="newPassword"
                        field={field}
                        meta={meta}
                        form={form}
                      />
                    )}
                  </Field>
                </div>
                <div className="col-12 col-md-6">
                  <Field name="passwordConfirmed">
                    {({ field, meta, form }: FieldProps) => (
                      <InputPassword
                        label={t("profile.repeat")}
                        placeholder={t("profile.repeat")}
                        id="passwordConfirmed"
                        field={field}
                        meta={meta}
                        form={form}
                      />
                    )}
                  </Field>
                </div>
              </Row>

              <p></p>
              <div className="row gx-4 mb-5 align-items-center">
                <div className="col-lg-9 pe-lg-0">
                  <div className="d-flex bg-soft-light-gray p-2 align-items-center rounded-3 text-gray mb-4 mb-lg-0">
                    <span className="me-2">
                      <ExclamationCircle />
                    </span>
                    <p className="mb-0 fs-7">
                      {t("profile.deleteDescription")}
                    </p>
                  </div>
                </div>
                <div className="col-lg-3 ps-lg-4">
                  <button
                    type="button"
                    disabled={deleteLoading}
                    onClick={() => setShowConfirmModal(true)}
                    className="btn btn-outline-danger w-100"
                  >
                    {t("profile.delete")}
                    {deleteLoading && (
                      <Spinner
                        animation="border"
                        role="status"
                        className="ms-2"
                      >
                        <span className="visually-hidden">
                          {t("profile.loading")}...
                        </span>
                      </Spinner>
                    )}
                  </button>
                </div>
              </div>
              <div className="col-lg-4 px-lg-4 mx-auto">
                <button
                  disabled={editLoading}
                  onClick={() => handleSubmit()}
                  className="btn btn-primary text-white w-100 py-3 fw-bold d-flex align-items-center justify-content-center"
                >
                  {t("profile.save")}
                  {editLoading && (
                    <Spinner animation="border" role="status" className="ms-2">
                      <span className="visually-hidden">
                        {t("profile.loading")}...
                      </span>
                    </Spinner>
                  )}
                </button>
              </div>
            </div>
            <CustomToast
              isShow={showToast}
              setShow={setShowToast}
              message={`${t("profile.saved")}`}
            />
          </Form>
        )}
      </Formik>
      <ConfirmModal
        show={showConfirmModal}
        title={t("profile.delete")}
        description={t("profile.confirmDelete")}
        secondDescription={t("profile.confirmDelete2")}
        confirmText={t("profile.deleteAction")}
        onConfirm={tryDeleteAccount}
        onCancel={() => setShowConfirmModal(false)}
      />
    </Container>
  );
};
export default Profile;
